import { customBaseQuery } from '@autone/ui';
import {
  createAllCacheEntriesUpdater,
  createAppQueryBuilder,
  Lock,
  sizeSorter,
} from '@autone/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  getAssortmentProductLocationsSchema,
  type TransformedAssortmentProductLocationsResponse,
} from './schemas/get-assortment-product-locations.schema';
import {
  getAssortmentSkuLocationsSchema,
  type TransformedAssortmentSkuLocationsResponse,
} from './schemas/get-assortment-sku-locations.schema';
import {
  type BulkEditAssortmentSkuLocationsRequest,
  type BulkEditAssortmentSkuLocationsResponse,
  type CreateAssortmentTemplateExportJobRequest,
  type CreateAssortmentTemplateExportJobResponse,
  type CreateAssortmentTemplateImportJobRequest,
  type CreateAssortmentTemplateImportJobResponse,
  type EditAssortmentProductLocationRequest,
  type EditAssortmentProductLocationResponse,
  type GetAssortmentCustomerConfigRequest,
  type GetAssortmentCustomerConfigResponse,
  type GetAssortmentFiltersRequest,
  type GetAssortmentFiltersResponse,
  type GetAssortmentProductLocationsInfiniteQueryPageParam,
  type GetAssortmentProductLocationsInfiniteQueryRequest,
  type GetAssortmentProductLocationSkusInfiniteQueryPageParam,
  type GetAssortmentProductLocationSkusInfiniteQueryRequest,
  type GetAssortmentProductsInfiniteQueryPageParam,
  type GetAssortmentProductsInfiniteQueryRequest,
  type GetAssortmentProductsRequest,
  type GetAssortmentProductsResponse,
  type GetAssortmentSkuLocationsInfiniteQueryPageParams,
  type GetAssortmentSkuLocationsInfiniteQueryRequest,
  type PollAssortmentTemplateExportJobRequest,
  type PollAssortmentTemplateExportJobResponse,
  type PollAssortmentTemplateImportJobRequest,
  type PollAssortmentTemplateImportJobResponse,
  type ValidateAssortmentTemplateExportLimitRequest,
  type ValidateAssortmentTemplateExportLimitResponse,
} from './types';
import {
  type EditAssortmentSkuLocationRequest,
  type EditAssortmentSkuLocationResponse,
} from './types/assortment-edit-sku-location.types';
import {
  type GetAssortmentSkuLocationsCountRequest,
  type GetAssortmentSkuLocationsCountResponse,
} from './types/assortment-get-sku-locations-count.types';
import {
  createDisableProductLocationSkusOpposingInputRecipe,
  createResetAllProductLocationSkusLoadingRecipe,
  createResetDisableProductLocationSkusRecipe,
  createResetProductLocationsLoadingRecipe,
  createSetAllProductLocationSkusLoadingRecipe,
  createSetProductLocationsLoadingRecipe,
} from './utilities/create-loading-state-recipes';
import {
  createUpdateSkuLocationCurrentAssortmentRecipe,
  createUpdateSkuLocationInitialAllocationRecipe,
} from './utilities/create-optimistic-update-sku-location-recipe';
import { createUpdateProductlocationAggregateValuesRecipe } from './utilities/create-update-product-location-aggregate-values-recipe';
import { createUpdateProductLocationSkuCountsRecipe } from './utilities/create-update-product-location-sku-counts-recipe';
import { createUpdateProductLocationSkusRecipe } from './utilities/create-update-product-location-skus-recipe';
import { assortmentReducerPath } from './utilities/types';

const TAG_TYPES = [
  'ASSORTMENT_PRODUCTS',
  'ASSORTMENT_PRODUCT_FILTERS',
  'ASSORTMENT_PRODUCT_LOCATIONS',
  'ASSORTMENT_PRODUCT_LOCATION_SKUS',
  'ASSORTMENT_SKU_LOCATIONS',
  'ASSORTMENT_TEMPLATE_EXPORT_JOB',
  'ASSORTMENT_TEMPLATE_IMPORT_JOB',
  'ASSORTMENT_TEMPLATE_EXPORT_LIMIT_VALIDATION',
  'ASSORTMENT_CUSTOMER_CONFIG',
] as const;

const API_PREFIX = '/assortment';

const assortmentQueryBuilder = createAppQueryBuilder<
  'assortment',
  typeof API_PREFIX
>();

/**
 * Async lock to sequentialise the processing of pessimistic updates
 * in a FIFO manner. Prevents race conditions on client state from requests
 * resolving out of the order they were dispatched.
 */
const lock = new Lock<string>();

/**
 * =========================
 * REGULAR APIs
 * =========================
 */

export const assortmentApi = createApi({
  reducerPath: assortmentReducerPath,
  tagTypes: TAG_TYPES,
  baseQuery: async (args, api, extraOptions) => {
    return customBaseQuery(args, api, extraOptions, `v2${API_PREFIX}`);
  },
  endpoints: (builder) => ({
    createAssortmentTemplateExportJob: builder.mutation<
      CreateAssortmentTemplateExportJobResponse,
      CreateAssortmentTemplateExportJobRequest
    >({
      query: assortmentQueryBuilder('post', '/export'),
      invalidatesTags: ['ASSORTMENT_TEMPLATE_EXPORT_JOB'],
    }),
    pollAssortmentTemplateExportJob: builder.query<
      PollAssortmentTemplateExportJobResponse,
      PollAssortmentTemplateExportJobRequest
    >({
      query: assortmentQueryBuilder('get', '/export/{job_id}'),
      providesTags: ['ASSORTMENT_TEMPLATE_EXPORT_JOB'],
    }),
    createAssortmentTemplateImportJob: builder.mutation<
      CreateAssortmentTemplateImportJobResponse,
      CreateAssortmentTemplateImportJobRequest
    >({
      query: assortmentQueryBuilder('post', '/import'),
      invalidatesTags: ['ASSORTMENT_TEMPLATE_IMPORT_JOB'],
    }),
    pollAssortmentTemplateImportJob: builder.query<
      PollAssortmentTemplateImportJobResponse,
      PollAssortmentTemplateImportJobRequest
    >({
      query: assortmentQueryBuilder('get', '/import/{job_id}'),
      providesTags: ['ASSORTMENT_TEMPLATE_IMPORT_JOB'],
    }),
    getAssortmentProducts: builder.query<
      GetAssortmentProductsResponse,
      GetAssortmentProductsRequest
    >({
      query: assortmentQueryBuilder('post', '/products'),
      providesTags: ['ASSORTMENT_PRODUCTS'],
    }),

    getAssortmentProductFilters: builder.query<
      GetAssortmentFiltersResponse,
      GetAssortmentFiltersRequest
    >({
      query: assortmentQueryBuilder('post', '/filters'),
      providesTags: ['ASSORTMENT_PRODUCT_FILTERS'],
      transformResponse: (response: GetAssortmentFiltersResponse) => {
        const { filters } = response;
        const sortedSizes = filters.sku?.sizes
          ? sizeSorter(filters.sku.sizes, 'description')
          : null;
        return {
          filters: {
            ...filters,
            sku: {
              ...filters.sku,
              sizes: sortedSizes,
            },
          },
        };
      },
    }),
    validateAssortmentTemplateExportLimit: builder.query<
      ValidateAssortmentTemplateExportLimitResponse,
      ValidateAssortmentTemplateExportLimitRequest
    >({
      query: assortmentQueryBuilder('post', '/export/validate-limit'),
      providesTags: ['ASSORTMENT_TEMPLATE_EXPORT_LIMIT_VALIDATION'],
    }),
    getAssortmentCustomerConfig: builder.query<
      GetAssortmentCustomerConfigResponse,
      GetAssortmentCustomerConfigRequest
    >({
      query: assortmentQueryBuilder('get', '/status'),
      providesTags: ['ASSORTMENT_CUSTOMER_CONFIG'],
    }),
    getAssortmentSkuLocationsCount: builder.query<
      GetAssortmentSkuLocationsCountResponse,
      GetAssortmentSkuLocationsCountRequest
    >({
      query: assortmentQueryBuilder('post', '/sku-locations/counts'),
      providesTags: ['ASSORTMENT_SKU_LOCATIONS'],
    }),
  }),
});

export const {
  useGetAssortmentProductsQuery,
  useGetAssortmentProductFiltersQuery,
  useLazyGetAssortmentProductFiltersQuery,
  useCreateAssortmentTemplateExportJobMutation,
  usePollAssortmentTemplateExportJobQuery,
  useCreateAssortmentTemplateImportJobMutation,
  usePollAssortmentTemplateImportJobQuery,
  useValidateAssortmentTemplateExportLimitQuery,
  useGetAssortmentCustomerConfigQuery,
  useLazyGetAssortmentCustomerConfigQuery,
  useGetAssortmentSkuLocationsCountQuery,
} = assortmentApi;

/**
 * =========================
 * INFINITE QUERY APIs
 * =========================
 */

export const assortmentInfiniteQueryApis = assortmentApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPaginatedAssortmentProducts: builder.infiniteQuery<
      GetAssortmentProductsResponse,
      GetAssortmentProductsInfiniteQueryRequest,
      GetAssortmentProductsInfiniteQueryPageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          cursor: undefined,
        },
        getNextPageParam: (lastPage) => {
          const {
            page_info: { next_cursor },
          } = lastPage;
          if (next_cursor) {
            return { cursor: next_cursor };
          }
          return undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = assortmentQueryBuilder('post', '/products');
        return queryFn({
          body,
          query: {
            ...query,
            cursor,
          },
        });
      },
      providesTags: ['ASSORTMENT_PRODUCTS'],
    }),
    getPaginatedAssortmentSkuLocations: builder.infiniteQuery<
      TransformedAssortmentSkuLocationsResponse,
      GetAssortmentSkuLocationsInfiniteQueryRequest,
      GetAssortmentSkuLocationsInfiniteQueryPageParams
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          cursor: undefined,
        },
        getNextPageParam: (lastPage) => {
          const {
            page_info: { next_cursor },
          } = lastPage;
          if (next_cursor) {
            return { cursor: next_cursor };
          }
          return undefined;
        },
      },
      query: ({ queryArg: { body, query }, pageParam: { cursor } }) => {
        const queryFn = assortmentQueryBuilder('post', '/sku-locations');
        return queryFn({
          body,
          query: {
            ...query,
            cursor,
          },
        });
      },
      providesTags: ['ASSORTMENT_SKU_LOCATIONS'],
      transformResponse: (response) =>
        getAssortmentSkuLocationsSchema.parse(response),
    }),
    editAssortmentSkuLocation: builder.mutation<
      EditAssortmentSkuLocationResponse,
      EditAssortmentSkuLocationRequest
    >({
      query: assortmentQueryBuilder(
        'patch',
        '/sku-locations/sku/{sku_id}/location/{location_id}',
      ),
      invalidatesTags: ['ASSORTMENT_PRODUCTS'],
      onQueryStarted: async ({ body, path }, mutationLifecycleApi) => {
        const { dispatch, queryFulfilled } = mutationLifecycleApi;
        const { sku_id, location_id } = path;
        const { assortment_update } = body;

        const updateAllSkuLocationCacheEntries = createAllCacheEntriesUpdater(
          assortmentInfiniteQueryApis,
          'getPaginatedAssortmentSkuLocations',
          mutationLifecycleApi,
        );
        const updateAllProductLocationSkusCacheEntries =
          createAllCacheEntriesUpdater(
            assortmentInfiniteQueryApis,
            'getPaginatedAssortmentProductLocationSkus',
            mutationLifecycleApi,
          );
        const updateAllProductLocationsCacheEntries =
          createAllCacheEntriesUpdater(
            assortmentInfiniteQueryApis,
            'getPaginatedAssortmentProductLocations',
            mutationLifecycleApi,
          );

        /**
         * Optimistically update IA and toggle state in both:
         * - `SkuLocationsTable`
         * - `ProductLocationSkusTable`
         *
         * based on the `update_type`
         */
        const optimisticUpdateRecipe =
          assortment_update.update_type === 'initial_allocation'
            ? createUpdateSkuLocationInitialAllocationRecipe(
                { skuId: sku_id, locationId: location_id },
                assortment_update.new_value,
              )
            : createUpdateSkuLocationCurrentAssortmentRecipe(
                { skuId: sku_id, locationId: location_id },
                assortment_update.new_value,
              );
        updateAllSkuLocationCacheEntries(optimisticUpdateRecipe);
        updateAllProductLocationSkusCacheEntries(optimisticUpdateRecipe);

        /**
         * Set `ProductLocationsTable` to loading state
         */
        updateAllProductLocationsCacheEntries(
          createSetProductLocationsLoadingRecipe(
            { locationId: location_id },
            assortment_update.update_type,
          ),
        );

        /**
         * Disable opposite input in `ProductLocationSkusTable`
         */
        updateAllProductLocationSkusCacheEntries(
          createDisableProductLocationSkusOpposingInputRecipe(
            { locationId: location_id, skuId: sku_id },
            assortment_update.update_type,
          ),
        );

        try {
          await lock.acquire(location_id);
          const { data } = await queryFulfilled;

          /**
           * Fire request to fetch the latest parent `ProductLocationsTable` row with new
           * aggregate IA and assorted/unassorted values. We need to do this because editing
           * the assorted status in lower aggregations will change the higher aggregated values.
           */
          const skuProduct = data.updated_sku_location.sku.product;
          const skuLocation = data.updated_sku_location.location;
          const result = await dispatch(
            assortmentInfiniteQueryApis.endpoints.getPaginatedAssortmentProductLocations.initiate(
              {
                path: {
                  product_id: skuProduct.id,
                },
                body: {
                  filters: {
                    locations: [
                      { id: skuLocation.id, description: skuLocation.desc },
                    ],
                  },
                },
                query: {},
              },
              { forceRefetch: true },
            ),
          );

          /**
           * If response is OK, update `ProductLocationsTable` with the updated row aggregate
           * values in-place to maintain sorting order.
           */
          const updatedProductLocation = result.data?.pages[0]?.items[0];
          if (updatedProductLocation) {
            const updateProductLocationsCacheEntries =
              createAllCacheEntriesUpdater(
                assortmentInfiniteQueryApis,
                'getPaginatedAssortmentProductLocations',
                mutationLifecycleApi,
                // only update cache entries that contain the relevant product
                (queryArg) => queryArg.path.product_id === skuProduct.id,
              );

            /**
             * Update `ProductLocationsTable` with the latest aggregate IA and assorted SKU counts.
             */
            updateProductLocationsCacheEntries(
              createUpdateProductlocationAggregateValuesRecipe(
                { locationId: location_id },
                updatedProductLocation,
              ),
            );
          } else {
            throw new Error('Updated product location not found in response');
          }
        } catch {
          dispatch(
            assortmentInfiniteQueryApis.util.invalidateTags([
              'ASSORTMENT_PRODUCTS',
              'ASSORTMENT_PRODUCT_LOCATIONS',
              'ASSORTMENT_PRODUCT_LOCATION_SKUS',
              'ASSORTMENT_SKU_LOCATIONS',
            ]),
          );
        } finally {
          /**
           * Reset `ProductLocationsTable` loading state.
           */
          updateAllProductLocationsCacheEntries(
            createResetProductLocationsLoadingRecipe(
              {
                locationId: location_id,
              },
              assortment_update.update_type,
            ),
          );
          /**
           * Reset `ProductLocationSkusTable` disabled state.
           */
          updateAllProductLocationSkusCacheEntries(
            createResetDisableProductLocationSkusRecipe({
              locationId: location_id,
              skuId: sku_id,
            }),
          );
          lock.release(location_id);
        }
      },
    }),
    bulkEditAssortmentSkuLocations: builder.mutation<
      BulkEditAssortmentSkuLocationsResponse,
      BulkEditAssortmentSkuLocationsRequest
    >({
      query: assortmentQueryBuilder('patch', '/sku-locations'),
      invalidatesTags: [
        'ASSORTMENT_SKU_LOCATIONS',
        'ASSORTMENT_PRODUCTS',
        'ASSORTMENT_PRODUCT_LOCATIONS',
        'ASSORTMENT_PRODUCT_LOCATION_SKUS',
      ],
    }),
    getPaginatedAssortmentProductLocations: builder.infiniteQuery<
      TransformedAssortmentProductLocationsResponse,
      GetAssortmentProductLocationsInfiniteQueryRequest,
      GetAssortmentProductLocationsInfiniteQueryPageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          cursor: undefined,
        },
        getNextPageParam: (lastPage) => {
          const {
            page_info: { next_cursor },
          } = lastPage;
          if (next_cursor) {
            return { cursor: next_cursor };
          }
          return undefined;
        },
      },
      query: ({ queryArg: { body, query, path }, pageParam: { cursor } }) => {
        const queryFn = assortmentQueryBuilder(
          'post',
          '/products/{product_id}/locations',
        );
        return queryFn({
          body,
          query: {
            ...query,
            cursor,
          },
          path,
        });
      },
      providesTags: ['ASSORTMENT_PRODUCT_LOCATIONS'],
      transformResponse: (response) =>
        getAssortmentProductLocationsSchema.parse(response),
    }),
    editAssortmentProductLocation: builder.mutation<
      EditAssortmentProductLocationResponse,
      EditAssortmentProductLocationRequest
    >({
      query: assortmentQueryBuilder(
        'patch',
        '/products/{product_id}/locations/{location_id}',
      ),
      invalidatesTags: ['ASSORTMENT_PRODUCTS', 'ASSORTMENT_SKU_LOCATIONS'],
      onQueryStarted: async ({ body, path }, mutationLifecycleApi) => {
        const { queryFulfilled, dispatch } = mutationLifecycleApi;
        const { product_id, location_id } = path;

        const updateProductLocationsCacheEntries = createAllCacheEntriesUpdater(
          assortmentInfiniteQueryApis,
          'getPaginatedAssortmentProductLocations',
          mutationLifecycleApi,
          // only entries with relevant `product_id`
          (queryArg) => queryArg.path.product_id === product_id,
        );
        const updateProductLocationSkusCacheEntry =
          createAllCacheEntriesUpdater(
            assortmentInfiniteQueryApis,
            'getPaginatedAssortmentProductLocationSkus',
            mutationLifecycleApi,
            // only entries with the relevant `product_id` x `location_id`
            (queryArg) =>
              queryArg.path.location_id === location_id &&
              queryArg.path.product_id === product_id,
          );

        /**
         * Optimistically update `ProductLocationsTable` sku_counts.
         * - If toggle `ON`, set to total skus
         * - If toggle `OFF`, set to 0
         */
        updateProductLocationsCacheEntries(
          createUpdateProductLocationSkuCountsRecipe(
            { locationId: location_id },
            body,
          ),
        );

        /**
         * Set all rows in `ProductLocationSkuTable` corresponding to the current
         * `location_id` x `product_id` to loading.
         */
        updateProductLocationSkusCacheEntry(
          createSetAllProductLocationSkusLoadingRecipe(),
        );

        try {
          await lock.acquire(location_id);
          await queryFulfilled;

          /**
           * Pessimistically update `ProductLocationSkusTable` once we know the request has succeeded.
           * We toggle on all SKUs for a product under the current location.
           */
          updateProductLocationSkusCacheEntry(
            createUpdateProductLocationSkusRecipe(body),
          );
        } catch {
          dispatch(
            assortmentInfiniteQueryApis.util.invalidateTags([
              'ASSORTMENT_PRODUCTS',
              'ASSORTMENT_PRODUCT_LOCATIONS',
              'ASSORTMENT_PRODUCT_LOCATION_SKUS',
              'ASSORTMENT_SKU_LOCATIONS',
            ]),
          );
        } finally {
          /**
           * reset `ProductLocationSkuTable` loading state
           */
          updateProductLocationSkusCacheEntry(
            createResetAllProductLocationSkusLoadingRecipe(),
          );
          lock.release(location_id);
        }
      },
    }),
    getPaginatedAssortmentProductLocationSkus: builder.infiniteQuery<
      TransformedAssortmentSkuLocationsResponse,
      GetAssortmentProductLocationSkusInfiniteQueryRequest,
      GetAssortmentProductLocationSkusInfiniteQueryPageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          cursor: undefined,
        },
        getNextPageParam: (lastPage) => {
          const {
            page_info: { next_cursor },
          } = lastPage;
          if (next_cursor) {
            return { cursor: next_cursor };
          }
          return undefined;
        },
      },
      query: ({ queryArg: { body, query, path }, pageParam: { cursor } }) => {
        const queryFn = assortmentQueryBuilder(
          'post',
          '/products/{product_id}/locations/{location_id}/skus',
        );
        return queryFn({
          query: {
            ...query,
            cursor,
          },
          body,
          path,
        });
      },
      providesTags: ['ASSORTMENT_PRODUCT_LOCATION_SKUS'],
      transformResponse: (response) => {
        const { page_info, items } =
          getAssortmentSkuLocationsSchema.parse(response);
        return {
          page_info,
          items: sizeSorter(items, 'size_id'),
        };
      },
    }),
  }),
});

export const {
  useGetPaginatedAssortmentProductsInfiniteQuery,
  useGetPaginatedAssortmentSkuLocationsInfiniteQuery,
  useGetPaginatedAssortmentProductLocationsInfiniteQuery,
  useGetPaginatedAssortmentProductLocationSkusInfiniteQuery,
  useEditAssortmentSkuLocationMutation,
  useEditAssortmentProductLocationMutation,
  useBulkEditAssortmentSkuLocationsMutation,
} = assortmentInfiniteQueryApis;
